// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { PaginationState, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";

export const IMPORTS_FILTER_DEFAULTS = {
  search: "",
  store: "",
  importType: "",
  source: "",
  status: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
};

export type ImportsFiltersState = ReturnType<typeof useImportsFiltersState>;

/**
 * Centralises pagination + sorting + search + filter state for an imports table, and
 * synchronises it back to a URL via the supplied `onSync` callback (typically `navigate({ ... f })`).
 */
export function useImportsFiltersState(initialFilters: string | undefined, onSync: (encoded: string | null) => void) {
  const initial = useMemo(() => decodeFilters(initialFilters, IMPORTS_FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState(initial.search);
  const [storeFilter, setStoreFilter] = useState(initial.store);
  const [importTypeFilter, setImportTypeFilter] = useState(initial.importType);
  const [sourceFilter, setSourceFilter] = useState(initial.source);
  const [statusFilter, setStatusFilter] = useState(initial.status);

  const resetToFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const syncUrl = useCallback(() => {
    const encoded = encodeFilters(
      {
        search,
        store: storeFilter,
        importType: importTypeFilter,
        source: sourceFilter,
        status: statusFilter,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
      IMPORTS_FILTER_DEFAULTS,
    );
    onSync(encoded || null);
  }, [
    search,
    storeFilter,
    importTypeFilter,
    sourceFilter,
    statusFilter,
    pagination.pageIndex,
    pagination.pageSize,
    onSync,
  ]);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  return {
    pagination,
    setPagination,
    sorting,
    setSorting,
    search,
    setSearch,
    storeFilter,
    setStoreFilter,
    importTypeFilter,
    setImportTypeFilter,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    resetToFirstPage,
  };
}
